import { useCallback, useMemo } from 'react'

import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { IDENTITY_CAPS, resolveVenueCaps, type VenueCaps } from '@/lib/configs/venue-capabilities'
import { useUserAccounts } from '@/service/account.hook'
import { useVenues } from '@/service/venue.hook'
import type { Exchange, TradeVenue } from '@/types/generated/common/types'

/**
 * Per-venue lookup over GetVenues. The returned function changes identity when
 * the venue list does, so put it (or what it returns) in memo/effect deps.
 */
export function useVenueCapsLookup() {
  const { data } = useVenues()
  return useCallback(
    (tv: TradeVenue | null | undefined) => resolveVenueCaps(data?.venues, tv),
    [data]
  )
}

export function useVenueCaps(tv: TradeVenue | null | undefined) {
  const lookup = useVenueCapsLookup()
  return useMemo(() => lookup(tv), [lookup, tv])
}

/**
 * Caps for an existing order or position, by the account it sits on (falling back to the
 * row's exchange). A row whose venue can't be resolved gets the identity caps, so amend and
 * close behave as before rather than being denied on a missing lookup.
 */
export function useRowVenueCaps() {
  const lookup = useVenueCapsLookup()
  const { data } = useUserAccounts()
  return useCallback(
    (accountId: string | undefined, exchange?: Exchange): VenueCaps => {
      const accountVenue = accountId
        ? data?.accounts.find((a) => a.accountId === accountId)?.tradeVenue
        : undefined
      const tv =
        accountVenue ?? (exchange === undefined ? undefined : exchangeToTradeVenue(exchange))
      return tv === undefined ? IDENTITY_CAPS : lookup(tv).caps
    },
    [lookup, data]
  )
}
